/**
 * Holding Card Component
 *
 * Displays a mutual fund holding with:
 * - Allocation edge (colored bar indicating portfolio allocation %)
 * - Fund name and category
 * - Current value with returns (absolute + %)
 * - Category delta comparison
 * - Score trajectory sparkline chart
 * - Fund rank display
 * - Score bar visualization (0-100)
 * - Health status badge
 */

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatPercent } from "@/lib/utils";
import { TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import type { FundRating, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Holding data for the card display
 */
export interface HoldingCardData {
  /** Unique identifier for the holding */
  id: string;
  /** Fund name */
  fundName: string;
  /** Fund category (e.g., "equity-large-cap") */
  category: string;
  /** AMC name */
  amcName?: string;
  /** Current value in INR */
  currentValue: number;
  /** Total returns in INR (absolute) */
  totalReturns: number;
  /** Total returns as percentage */
  totalReturnsPercent: number;
  /** Fund's 1Y returns */
  returns1Y: number;
  /** Category average returns (for delta calculation) */
  categoryAverageReturns?: number;
  /** Current fund rank in category */
  rank: number;
  /** Total funds in category */
  totalFundsInCategory: number;
  /** Health status rating */
  rating: FundRating;
  /** Consistency score (0-100) - optional, only available when viewing detail */
  consistencyScore?: number;
  /** Recency score (0-100) */
  recencyScore?: number;
  /** Volatility score (0-100) */
  volatilityScore?: number;
  /** Rank history for trajectory chart */
  rankHistory?: RankHistoryEntry[];
  /** Portfolio allocation percentage (0-100) */
  allocationPercent: number;
}

/**
 * Props for the HoldingCard component
 */
export interface HoldingCardProps {
  /** Holding data */
  data: HoldingCardData;
  /** Click handler for navigation */
  onClick?: (holdingId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Minimum visible height for the allocation edge (percentage).
 * Set to 20% to ensure even small allocations have a visible colored bar.
 */
const ALLOCATION_EDGE_MIN_HEIGHT = 20;

/**
 * Maximum height for the allocation edge (percentage).
 * Capped at 100% to fill the entire card height for large allocations.
 */
const ALLOCATION_EDGE_MAX_HEIGHT = 100;

/**
 * Multiplier to convert allocation percentage to visual height.
 * With a multiplier of 4, a 25% allocation results in 100% height,
 * providing good visual differentiation across typical allocation ranges.
 */
const ALLOCATION_HEIGHT_MULTIPLIER = 4;

/**
 * Status configuration with colors and labels
 */
const STATUS_CONFIG: Record<
  FundRating,
  {
    label: string;
    bgColor: string;
    textColor: string;
    badgeBg: string;
    badgeText: string;
    edgeColor: string;
    /** Hex color value for SVG strokes (e.g., sparkline charts) */
    strokeColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    bgColor: "bg-emerald-500",
    textColor: "text-emerald-700 dark:text-emerald-400",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/30",
    badgeText: "text-emerald-700 dark:text-emerald-400",
    edgeColor: "bg-emerald-500",
    strokeColor: "#10b981",
  },
  "on-track": {
    label: "On-Track",
    bgColor: "bg-sky-500",
    textColor: "text-sky-700 dark:text-sky-400",
    badgeBg: "bg-sky-100 dark:bg-sky-900/30",
    badgeText: "text-sky-700 dark:text-sky-400",
    edgeColor: "bg-sky-500",
    strokeColor: "#0ea5e9",
  },
  "off-track": {
    label: "Off-Track",
    bgColor: "bg-amber-500",
    textColor: "text-amber-700 dark:text-amber-400",
    badgeBg: "bg-amber-100 dark:bg-amber-900/30",
    badgeText: "text-amber-700 dark:text-amber-400",
    edgeColor: "bg-amber-500",
    strokeColor: "#f59e0b",
  },
  "out-of-form": {
    label: "Out-of-Form",
    bgColor: "bg-rose-500",
    textColor: "text-rose-700 dark:text-rose-400",
    badgeBg: "bg-rose-100 dark:bg-rose-900/30",
    badgeText: "text-rose-700 dark:text-rose-400",
    edgeColor: "bg-rose-500",
    strokeColor: "#f43f5e",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format category name for display
 */
function formatCategory(category: string): string {
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Calculate category delta
 */
function calculateCategoryDelta(
  returns1Y: number,
  categoryAverage?: number
): { delta: number; isPositive: boolean } | null {
  if (categoryAverage === undefined) return null;
  const delta = returns1Y - categoryAverage;
  return { delta, isPositive: delta >= 0 };
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Allocation edge - colored bar on the left indicating portfolio allocation
 */
function AllocationEdge({
  allocationPercent,
  rating,
}: {
  allocationPercent: number;
  rating: FundRating;
}) {
  const config = STATUS_CONFIG[rating];
  // Height is proportional to allocation, clamped between min and max
  const heightPercent = Math.max(
    ALLOCATION_EDGE_MIN_HEIGHT,
    Math.min(ALLOCATION_EDGE_MAX_HEIGHT, allocationPercent * ALLOCATION_HEIGHT_MULTIPLIER)
  );

  return (
    <div className="absolute left-0 top-0 bottom-0 w-1 flex flex-col justify-end overflow-hidden rounded-l-xl">
      <div
        className={cn(config.edgeColor, "w-full transition-all duration-300")}
        style={{ height: `${heightPercent}%` }}
        title={`${allocationPercent.toFixed(1)}% of portfolio`}
      />
    </div>
  );
}

/**
 * Status badge showing health status
 */
function StatusBadge({ rating }: { rating: FundRating }) {
  const config = STATUS_CONFIG[rating];
  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-medium border-0",
        config.badgeBg,
        config.badgeText
      )}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Score bar visualization (0-100 scale)
 */
function ScoreBar({
  score,
  label,
  rating,
}: {
  score: number;
  label?: string;
  rating: FundRating;
}) {
  const config = STATUS_CONFIG[rating];
  const clampedScore = Math.max(0, Math.min(100, score));

  return (
    <div className="space-y-1">
      {label && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{label}</span>
          <span className="font-medium">{clampedScore}</span>
        </div>
      )}
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(config.bgColor, "h-full rounded-full transition-all duration-500")}
          style={{ width: `${clampedScore}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Score trajectory sparkline chart
 * Shows rank trend over time (lower rank = better, so we invert for visual)
 */
function ScoreTrajectory({
  rankHistory,
  rating,
}: {
  rankHistory?: RankHistoryEntry[];
  rating: FundRating;
}) {
  // If no history, show placeholder
  if (!rankHistory || rankHistory.length < 2) {
    return (
      <div className="h-8 w-16 flex items-center justify-center">
        <span className="text-xs text-muted-foreground">--</span>
      </div>
    );
  }

  const config = STATUS_CONFIG[rating];

  // Take last 12 data points for the sparkline
  const recentHistory = rankHistory.slice(-12);

  // Calculate percentile (inverted so lower rank = higher position)
  const points = recentHistory.map((entry) => {
    const percentile = (entry.rank / entry.totalFunds) * 100;
    return 100 - percentile; // Invert so better rank = higher on chart
  });

  // Normalize to SVG coordinates
  const width = 64;
  const height = 32;
  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const minY = Math.min(...points);
  const maxY = Math.max(...points);
  const range = maxY - minY || 1;

  const pathPoints = points.map((point, index) => {
    const x = padding + (index / (points.length - 1)) * chartWidth;
    const y = padding + ((maxY - point) / range) * chartHeight;
    return `${x},${y}`;
  });

  const pathD = `M ${pathPoints.join(" L ")}`;

  // Determine trend
  const firstHalf = points.slice(0, Math.floor(points.length / 2));
  const secondHalf = points.slice(Math.floor(points.length / 2));
  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  const isImproving = secondAvg > firstAvg;

  return (
    <div
      className="relative"
      title={isImproving ? "Improving trend" : "Declining trend"}
    >
      <svg
        width={width}
        height={height}
        className="overflow-visible"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Background grid lines */}
        <line
          x1={padding}
          y1={height / 2}
          x2={width - padding}
          y2={height / 2}
          stroke="currentColor"
          strokeOpacity={0.1}
          strokeDasharray="2,2"
        />
        {/* Sparkline path */}
        <path
          d={pathD}
          fill="none"
          stroke={config.strokeColor}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* End point dot */}
        <circle
          cx={width - padding}
          cy={padding + ((maxY - (points[points.length - 1] ?? 0)) / range) * chartHeight}
          r={3}
          fill={config.strokeColor}
        />
      </svg>
    </div>
  );
}

/**
 * Rank display with category context
 */
function RankDisplay({
  rank,
  totalFunds,
  rating,
}: {
  rank: number;
  totalFunds: number;
  rating: FundRating;
}) {
  const config = STATUS_CONFIG[rating];
  return (
    <div className="flex items-baseline gap-1">
      <span className={cn("text-sm font-semibold", config.textColor)}>
        #{rank}
      </span>
      <span className="text-xs text-muted-foreground">/ {totalFunds}</span>
    </div>
  );
}

/**
 * Category delta display
 */
function CategoryDelta({ returns1Y, categoryAverage }: { returns1Y: number; categoryAverage?: number }) {
  const delta = calculateCategoryDelta(returns1Y, categoryAverage);
  if (!delta) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 text-xs",
        delta.isPositive ? "text-green-600" : "text-red-600"
      )}
    >
      {delta.isPositive ? (
        <TrendingUp className="h-3 w-3" />
      ) : (
        <TrendingDown className="h-3 w-3" />
      )}
      <span>
        {delta.isPositive ? "+" : ""}
        {delta.delta.toFixed(1)}% vs category
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Holding Card
 *
 * Displays a comprehensive view of a mutual fund holding with visual indicators
 * for performance, allocation, and health status.
 *
 * @example
 * ```tsx
 * <HoldingCard
 *   data={{
 *     id: "hld_001",
 *     fundName: "Parag Parikh Flexi Cap Fund",
 *     category: "equity-flexi-cap",
 *     currentValue: 98110,
 *     totalReturns: 20167,
 *     totalReturnsPercent: 25.87,
 *     returns1Y: 22.78,
 *     categoryAverageReturns: 18.5,
 *     rank: 1,
 *     totalFundsInCategory: 32,
 *     rating: "in-form",
 *     consistencyScore: 94,
 *     allocationPercent: 18.2,
 *   }}
 *   onClick={(id) => navigate(`/holdings/${id}`)}
 * />
 * ```
 */
export function HoldingCard({
  data,
  onClick,
  className,
}: HoldingCardProps) {
  const isPositiveReturn = data.totalReturns >= 0;
  const config = STATUS_CONFIG[data.rating];

  const handleClick = () => {
    onClick?.(data.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      className={cn(
        "relative w-full overflow-hidden transition-all duration-200",
        onClick && "cursor-pointer hover:shadow-md hover:border-muted-foreground/20",
        className
      )}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      aria-label={onClick ? `View details for ${data.fundName}` : undefined}
    >
      {/* Allocation Edge */}
      <AllocationEdge
        allocationPercent={data.allocationPercent}
        rating={data.rating}
      />

      <CardContent className="pl-4 py-4">
        <div className="flex flex-col gap-3">
          {/* Header Row: Fund Name + Status Badge */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm leading-tight truncate">
                {data.fundName}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatCategory(data.category)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusBadge rating={data.rating} />
              {onClick && (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Value Row: Current Value + Returns */}
          <div className="flex items-baseline justify-between gap-2">
            <div>
              <p className="text-lg font-bold tracking-tight">
                {formatINR(data.currentValue)}
              </p>
              <div
                className={cn(
                  "flex items-center gap-1 text-sm",
                  isPositiveReturn ? "text-green-600" : "text-red-600"
                )}
              >
                {isPositiveReturn ? (
                  <TrendingUp className="h-3.5 w-3.5" />
                ) : (
                  <TrendingDown className="h-3.5 w-3.5" />
                )}
                <span>
                  {formatINR(data.totalReturns)} ({formatPercent(data.totalReturnsPercent)})
                </span>
              </div>
            </div>

            {/* Category Delta */}
            <CategoryDelta
              returns1Y={data.returns1Y}
              categoryAverage={data.categoryAverageReturns}
            />
          </div>

          {/* Bottom Row: Trajectory, Rank, Score */}
          <div className="flex items-center justify-between gap-4 pt-1 border-t border-border/50">
            {/* Score Trajectory Chart */}
            <div className="flex items-center gap-2">
              <ScoreTrajectory
                rankHistory={data.rankHistory}
                rating={data.rating}
              />
              <RankDisplay
                rank={data.rank}
                totalFunds={data.totalFundsInCategory}
                rating={data.rating}
              />
            </div>

            {/* Score Bar - only shown when consistencyScore is available */}
            {data.consistencyScore !== undefined && (
              <div className="flex-1 max-w-[120px]">
                <ScoreBar
                  score={data.consistencyScore}
                  label="Score"
                  rating={data.rating}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for HoldingCard
 */
export function HoldingCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("relative w-full overflow-hidden", className)}>
      {/* Allocation Edge Skeleton */}
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl">
        <Skeleton className="w-full h-1/2 mt-auto" />
      </div>

      <CardContent className="pl-4 py-4">
        <div className="flex flex-col gap-3">
          {/* Header Row Skeleton */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>

          {/* Value Row Skeleton */}
          <div className="flex items-baseline justify-between gap-2">
            <div className="space-y-1.5">
              <Skeleton className="h-6 w-28" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-3 w-20" />
          </div>

          {/* Bottom Row Skeleton */}
          <div className="flex items-center justify-between gap-4 pt-1 border-t border-border/50">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-4 w-12" />
            </div>
            <div className="flex-1 max-w-[120px] space-y-1">
              <div className="flex justify-between">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-6" />
              </div>
              <Skeleton className="h-1.5 w-full rounded-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default HoldingCard;
